import React from 'react';
import { useUserInfo } from '@/provider/UserInfoProvider';
import useRoute from '@/ueshook/useRoute';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  UserCircle,
  Images,
  Invoice,
  Gift,
  Question,
  SignOut,
  X,
} from '@phosphor-icons/react';
import Image from 'next/image';

interface UserMenuMobileProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenUserInfo: () => void;
  onLogout: () => void;
}

const UserMenuMobile: React.FC<UserMenuMobileProps> = ({
  isOpen,
  onClose,
  onOpenUserInfo,
  onLogout,
}) => {
  const { userInfo, isLoadingUser } = useUserInfo();
  const { push } = useRoute();

  const handleUpgrade = () => {
    push('/pricing');
    onClose();
  };

  const handleMyProjects = () => {
    push('/my-projects');
    onClose();
  };

  const handlePlansAndPricing = () => {
    push('/pricing');
    onClose();
  };

  const handleInviteFriends = () => {
    push('/invite');
    onClose();
  };

  const handleGetHelp = () => {
    push('/get-help');
    onClose();
  };

  const handleAccount = () => {
    onOpenUserInfo();
    onClose();
  };

  const handleLogout = () => {
    onLogout();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 z-50 flex h-screen w-full flex-col bg-white md:hidden'>
      {/* 顶部导航条 */}
      <div className='border-b border-gray-200'>
        <div className='flex h-[70px] items-center justify-between px-8'>
          <div>
            <Image
              src='https://imagedelivery.net/7rExjEZEypWvglDwZ5-zxA/ffee010c-d40e-437a-e26f-ecd79eda1b00/public'
              alt='Kaze AI Logo'
              className='h-[1.875rem] w-[6.25rem] cursor-pointer'
              width={100}
              height={30}
              onClick={() => {
                push('/');
                onClose();
              }}
            />
          </div>
          <button
            className='flex h-[29px] w-[29px] items-center justify-center rounded-full'
            onClick={onClose}
            type='button'
          >
            <X size={20} color='black' />
          </button>
        </div>
      </div>

      {/* 主要内容区域 */}
      <div className='flex-1 overflow-y-auto px-6 py-6'>
        {isLoadingUser ? (
          <div className='flex items-center justify-center'>
            <p className='text-black'>Loading user info...</p>
          </div>
        ) : userInfo ? (
          <>
            {/* 积分/计划信息 */}
            <div className='mb-6 rounded-lg border border-gray-200 p-4'>
              {(() => {
                const items = [];

                // 添加 credits remaining 如果大于0
                if (userInfo.plan.available_count > 0) {
                  items.push({
                    id: 'credits',
                    value: userInfo.plan.available_count,
                    text: 'credits remaining',
                  });
                }

                // 添加 AI Headshot Pro 如果大于0
                if (userInfo.plan.id_photo.pro_limit > 0) {
                  items.push({
                    id: 'pro',
                    value: userInfo.plan.id_photo.pro_limit,
                    text: 'AI Headshot Pro',
                  });
                }

                // 添加 AI Headshot Basic 如果大于0
                if (userInfo.plan.id_photo.basic_limit > 0) {
                  items.push({
                    id: 'basic',
                    value: userInfo.plan.id_photo.basic_limit,
                    text: 'AI Headshot Basic',
                  });
                }

                // 如果没有任何项目，显示默认信息
                if (items.length === 0) {
                  return (
                    <div className='text-center text-gray-500'>
                      No credits available
                    </div>
                  );
                }

                // 渲染项目列表，在项目之间添加分隔线
                return items.map((item, index) => (
                  <React.Fragment key={item.id}>
                    <div className='flex gap-1'>
                      <span className='text-xl font-bold text-[#6137E0]'>
                        {item.value}
                      </span>
                      <span className='text-lg font-medium text-gray-800'>
                        {item.text}
                      </span>
                    </div>
                    {index < items.length - 1 && <Separator className='my-2' />}
                  </React.Fragment>
                ));
              })()}
            </div>

            {/* 菜单项 */}
            <div className='space-y-1'>
              <Button
                variant='ghost'
                className='w-full justify-start gap-3 rounded-lg px-0 text-left text-xl font-medium text-gray-900'
                onClick={handleAccount}
              >
                <UserCircle size={28} />
                Account
              </Button>

              <Button
                variant='ghost'
                className='w-full justify-start gap-3 rounded-lg px-0 text-left text-xl font-medium text-gray-900'
                onClick={handleMyProjects}
              >
                <Images size={28} />
                My projects
              </Button>

              <Button
                variant='ghost'
                className='w-full justify-start gap-3 rounded-lg px-0 text-left text-xl font-medium text-gray-900'
                onClick={handlePlansAndPricing}
              >
                <Invoice size={28} />
                Plans and pricing
              </Button>

              <Button
                variant='ghost'
                className='w-full justify-start gap-3 rounded-lg px-0 text-left text-xl font-medium text-gray-900'
                onClick={handleInviteFriends}
              >
                <Gift size={28} />
                Invite friends, earn credits
              </Button>

              <Button
                variant='ghost'
                className='w-full justify-start gap-3 rounded-lg px-0 text-left text-xl font-medium text-gray-900'
                onClick={handleGetHelp}
              >
                <Question size={28} />
                Get help
              </Button>
            </div>
          </>
        ) : (
          <div className='flex items-center justify-center'>
            <p className='text-black'>Loading user info...</p>
          </div>
        )}
      </div>

      {/* 底部固定区域 */}
      <div className='sticky bottom-0 bg-white pb-[calc(1rem+env(safe-area-inset-bottom))] pt-4'>
        <div className='flex flex-col gap-3 px-6'>
          <Button
            className='w-full bg-[#6137E0] text-white hover:bg-[#6c45e0]'
            onClick={handleUpgrade}
          >
            Upgrade
          </Button>
          <Button variant='outline' className='w-full' onClick={handleLogout}>
            Log out
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UserMenuMobile;
